/* Unveränderliche Bearbeitungshilfen für die Formulardefinition im Editor */
import { flattenQuestions, formId, type FormDefinition, type FormItem, type FormPage, type FormQuestion, type FormQuestionType, type FormSection } from '@kaba/shared';

export type Def = FormDefinition;

export function newQuestion(type: FormQuestionType): FormQuestion {
  const q: FormQuestion = { kind: 'question', id: formId('q'), type, label: '' };
  if (type === 'choice' || type === 'multichoice')
    q.options = [
      { id: formId('o'), label: 'Antwort 1' },
      { id: formId('o'), label: 'Antwort 2' },
    ];
  if (type === 'choice') q.display = 'auto';
  if (type === 'number') q.decimals = 0;
  if (type === 'file') {
    q.maxFiles = 3;
    q.maxSizeMb = 10;
    q.accept = ['image', 'pdf'];
  }
  if (type === 'calc') q.decimals = 2;
  if (type === 'info') q.label = 'Hinweis';
  return q;
}

export function newSection(repeat: boolean): FormSection {
  return {
    kind: 'section',
    id: formId('s'),
    title: repeat ? 'Positionen' : 'Abschnitt',
    repeat: repeat ? { min: 1, max: 10, itemLabel: 'Position' } : null,
    items: [newQuestion('text')].map((q) => ({ ...q, label: repeat ? 'Bezeichnung' : 'Frage' })),
  };
}

/** Wo liegt ein Element? */
export function locate(def: Def, id: string): { page: FormPage; section: FormSection | null; item: FormItem } | null {
  for (const page of def.pages)
    for (const it of page.items) {
      if (it.id === id) return { page, section: null, item: it };
      if (it.kind === 'section') {
        const q = it.items.find((x) => x.id === id);
        if (q) return { page, section: it, item: q };
      }
    }
  return null;
}

export function updateItem(def: Def, id: string, fn: (it: FormItem) => FormItem): Def {
  return {
    ...def,
    pages: def.pages.map((p) => ({
      ...p,
      items: p.items.map((it) => {
        if (it.id === id) return fn(it);
        if (it.kind === 'section' && it.items.some((q) => q.id === id)) return { ...it, items: it.items.map((q) => (q.id === id ? (fn(q) as FormQuestion) : q)) };
        return it;
      }),
    })),
  };
}

export function updatePage(def: Def, id: string, fn: (p: FormPage) => FormPage): Def {
  return { ...def, pages: def.pages.map((p) => (p.id === id ? fn(p) : p)) };
}

export function removeItem(def: Def, id: string): Def {
  return {
    ...def,
    pages: def.pages.map((p) => ({
      ...p,
      items: p.items.filter((it) => it.id !== id).map((it) => (it.kind === 'section' ? { ...it, items: it.items.filter((q) => q.id !== id) } : it)),
    })),
  };
}

/** Innerhalb der Liste (Seite oder Abschnitt) um eins verschieben */
export function moveItem(def: Def, id: string, d: -1 | 1): Def {
  const swap = <T extends { id: string }>(arr: T[]): T[] => {
    const i = arr.findIndex((x) => x.id === id);
    if (i < 0 || i + d < 0 || i + d >= arr.length) return arr;
    const n = [...arr];
    [n[i], n[i + d]] = [n[i + d]!, n[i]!];
    return n;
  };
  return {
    ...def,
    pages: def.pages.map((p) => ({ ...p, items: swap(p.items).map((it) => (it.kind === 'section' ? { ...it, items: swap(it.items) } : it)) })),
  };
}

export function moveToPage(def: Def, id: string, pageId: string): Def {
  const loc = locate(def, id);
  if (!loc) return def;
  const without = removeItem(def, id);
  return updatePage(without, pageId, (p) => ({ ...p, items: [...p.items, loc.item] }));
}

/** Kopie mit neuen IDs (auch Antwortoptionen) direkt dahinter */
export function duplicateItem(def: Def, id: string): Def {
  const loc = locate(def, id);
  if (!loc) return def;
  const copyQ = (q: FormQuestion): FormQuestion => ({ ...structuredClone(q), id: formId('q'), label: `${q.label} (Kopie)`, options: q.options?.map((o) => ({ ...o, id: formId('o') })) });
  const copy: FormItem = loc.item.kind === 'section' ? { ...structuredClone(loc.item), id: formId('s'), title: `${loc.item.title} (Kopie)`, items: loc.item.items.map(copyQ) } : copyQ(loc.item);
  const insert = <T extends { id: string }>(arr: T[], x: T) => {
    const i = arr.findIndex((y) => y.id === id);
    return [...arr.slice(0, i + 1), x, ...arr.slice(i + 1)];
  };
  return {
    ...def,
    pages: def.pages.map((p) => ({
      ...p,
      items: p.items.some((it) => it.id === id) ? insert(p.items, copy) : p.items.map((it) => (it.kind === 'section' && it.items.some((q) => q.id === id) ? { ...it, items: insert(it.items, copy as FormQuestion) } : it)),
    })),
  };
}

export interface Ref {
  id: string;
  label: string;
  type: FormQuestionType | 'section';
  q?: FormQuestion;
}

/**
 * Fragen, auf die sich eine Bedingung an dieser Stelle beziehen darf (R03): alle früheren Fragen;
 * in wiederholten Abschnitten zusätzlich frühere Felder derselben Zeile. Abschnitte als „Anzahl Einträge“.
 */
export function refsBefore(def: Def, at: string | 'end'): Ref[] {
  const out: Ref[] = [];
  for (const p of def.pages) {
    if (p.id === at) return out;
    for (const it of p.items) {
      if (it.id === at) return out;
      if (it.kind === 'section') {
        if (it.repeat) {
          const inside = it.items.findIndex((q) => q.id === at);
          if (inside >= 0) return [...out, ...it.items.slice(0, inside).filter((q) => q.type !== 'info').map((q) => ({ id: q.id, label: q.label || 'Ohne Beschriftung', type: q.type, q }))];
          out.push({ id: it.id, label: it.title, type: 'section' });
          continue;
        }
        for (const q of it.items) {
          if (q.id === at) return out;
          if (q.type !== 'info') out.push({ id: q.id, label: q.label || 'Ohne Beschriftung', type: q.type, q });
        }
        continue;
      }
      if (it.type !== 'info') out.push({ id: it.id, label: it.label || 'Ohne Beschriftung', type: it.type, q: it });
    }
  }
  return out;
}

/** Zahlenfragen für Formeln: frühere Zahlen; SUMME über Zahlen in früheren wiederholten Abschnitten */
export function formulaRefs(def: Def, at: string): { refs: Ref[]; sums: Ref[] } {
  const refs = refsBefore(def, at).filter((r) => r.type === 'number' || r.type === 'currency' || r.type === 'calc' || r.type === 'rating');
  const sums: Ref[] = [];
  for (const p of def.pages) {
    for (const it of p.items) {
      if (it.id === at || (it.kind === 'section' && it.items.some((q) => q.id === at))) return { refs, sums };
      if (it.kind === 'section' && it.repeat)
        for (const q of it.items) if (q.type === 'number' || q.type === 'currency' || q.type === 'calc') sums.push({ id: q.id, label: q.label, type: q.type, q });
    }
  }
  return { refs, sums };
}

export const allQuestions = (def: Def) => flattenQuestions(def).map((f) => f.q);

/** Titelvorlage: {{q:id}} ↔ [Beschriftung] */
export function titleToText(def: Def, t: string): string {
  const qs = allQuestions(def);
  return t.replace(/\{\{q:([\w-]+)\}\}/g, (_m, id: string) => `[${qs.find((q) => q.id === id)?.label ?? '?'}]`);
}
export function textToTitle(def: Def, text: string): { title: string; unknown: string[] } {
  const qs = allQuestions(def);
  const unknown: string[] = [];
  const title = text.replace(/\[([^\]]+)\]/g, (m, label: string) => {
    const q = qs.find((x) => x.label.trim().toLowerCase() === label.trim().toLowerCase());
    if (!q) {
      unknown.push(label);
      return m;
    }
    return `{{q:${q.id}}}`;
  });
  return { title, unknown };
}
